import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStateValue } from './StateProvider';
import { getPermission } from './Utils/permissions.js';
import moment from 'moment';
import axios from 'axios';
import configData from './Config';
import FloatingButton from './Components/floatingbutton.js';

import Header from './Header';
import Summary from './Summary';
import Assets from './Assets';
import Structure from './Structure';
import Readings from './Readings.js';
import Users from './Users.js';
import Widgets from './Widgets';
import Settings from './Settings';
import Reports from './Reports';

import { getHomePermission } from './Utils/permissions.js';

import './Home.css';

const smallscreen = 540;

function Home({ state, setState }) {
  const [{ projects, project, version }, dispatch] = useStateValue();

  const [togglemenu, setTogglemenu] = useState(false);
  const [screenwidth, setScreenWidth] = useState(window.innerWidth);

  const [assets, setAssets] = useState([]);
  const [clients, setClients] = useState([]);
  const [readings, setReadings] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [users, setUsers] = useState([]);

  const [option, setOption] = useState(<Summary state={state} setState={setState} />);

  const homemenuRef = useRef(null);
  const navigation = useNavigate();

  useEffect(() => {
    if (project.projectid == null) navigation('/')
  }, []);

  useEffect(() => {
    setAssets(project.assets)
    setReadings(project.readings);
    setUsers(project?.users);
    setWidgets(project.widgets);
  }, [project]);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCloseModal = () => {
    setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null }));
  }

  const editProjectButton = () => {
    setState(state => ({ ...state, modalopen: true, modalcontent: <EditProject />, modaltype: 'small', modaltitle: 'Edit Project' }));
  }

  const archiveProjectButton = () => {
    setState(state => ({ ...state, modalopen: true, modalcontent: <ArchiveProject />, modaltype: 'small', modaltitle: 'Archive Project' }));
  }

  const unarchiveProjectButton = () => {
    setState(state => ({ ...state, modalopen: true, modalcontent: <UnarchiveProject />, modaltype: 'small', modaltitle: 'Unarchive Project' }));
  }

  const removeButton = () => {
    setState(state => ({ ...state, modalopen: true, modalcontent: <RemoveProject />, modaltype: 'small', modaltitle: 'Remove Project' }));
  }

  const disconnectButton = () => {
    setState(state => ({ ...state, modalopen: true, modalcontent: <DisconnectProject />, modaltype: 'small', modaltitle: 'Disconnect Project' }));
  }

  function EditProject() {
    const [name, setName] = useState('');

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState('');

    useEffect(() => {
      setName(project.name)
    }, [project.name]);

    const updateProject = () => {
      console.log('Trying to update project');

      setResult('');

      const hasPermission = getPermission(
        project.projectuserid,
        project.users,
        state.user.userid,
        'Project',
        'edit',
        project.archived
      );
      if (hasPermission.code == 0) {
        setResult(hasPermission.data);
        return;
      }

      const trimmedname = name.trim();
      if (!trimmedname) {
        setResult('Project name cannot be empty');
        return;
      }

      const projectExists = projects.some(project => project.name.toLowerCase() === trimmedname.toLowerCase());
      if (projectExists) {
        setResult('Project name already exists');
        return;
      }

      setLoading(true);

      const data = {
        projectuserid: project.projectuserid,
        projectid: project.projectid,
        name: trimmedname,
        size: ''
      }

      axios.post(configData.CONTROLLERURL + configData.UPDATEPROJECT, data, {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json;charset=UTF-8",
          "userid": state.user.userid,
          "usertoken": state.user.usertoken
        }
      }).then((res) => {
        console.log('Add project data received')
        console.log(res.data)
        if (res.data instanceof Object) {
          if (res.data.code === 1) {
            dispatch({
              type: 'UPDATE_PROJECT',
              project: data
            });
            handleCloseModal();
            navigation('/');
          }
          else {
            setResult(res.data.data);
          }
        }
      }).catch((err) => {
        setResult(err.response?.data?.message || err.message || 'Network error');
      }).finally(() => {
        setLoading(false)
      })
    }

    return (
      <div className='modal_body'>
        <div className='modal_printable'>
          <div className='modal_row'>
            <div className='modal_rowsection'>
              <input
                className='modal_input'
                type="text"
                placeholder='Name'
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className='modal_actions'>
          {result && <div className='modal_result'>{result}</div>}
          <div className='modal_buttons'>
            <div className='modal_buttoncontainer'>
              <button className="modal_button" onClick={() => updateProject()} disabled={loading}>Update</button>
            </div>
            <div className='modal_buttoncontainer'>
              <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const downloadBackup = () => {
    if (project) {
      project.version = version;
      const projectJSON = JSON.stringify(project);
      const blob = new Blob([projectJSON], { type: 'application/json' });

      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toLocaleDateString('en-GB');
      const formattedDate = today.replace(/\//g, '_');
      const formattedName = `${project.name.toLowerCase().replace(/\s+/g, '_')}_${formattedDate}`;
      a.download = formattedName + '.pd';
      document.body.appendChild(a);

      a.click();

      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }

  function ArchiveProject() {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState('');

    const archiveProject = async () => {
      console.log('Trying to archive project');

      setResult('');

      const hasPermission = getPermission(
        project.projectuserid,
        project.users,
        state.user.userid,
        'Archive',
        'add',
        project.archived
      );
      if (hasPermission.code == 0) {
        setResult(hasPermission.data);
        return;
      }

      setLoading(true);

      const allemails = clients.flatMap(client => [{ id: client.id, type: 'client', email: client.email }, ...client.devices.map(device => ({ id: device.id, type: 'device', email: device.email }))]).filter(({ email }) => email);

      const data = {
        projectuserid: project.projectuserid,
        projectid: project.projectid,
        emails: allemails.map(({ email }) => email)
      }

      await axios.post(configData.CONTROLLERURL + configData.GETUSERSSTATUS, data, {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json;charset=UTF-8",
          "userid": state.user.userid,
          "usertoken": state.user.usertoken
        }
      }).then(async (statusRes) => {
        console.log('Clients status data received');
        console.log(statusRes.data);

        if (statusRes.data.some(status => status.connected)) {
          setResult('Some clients are still connected. Cannot archive.');
          setLoading(false);
          return;
        }
        else {
          console.log('No connected clients found')
        }

        const archiveData = {
          projectuserid: project.projectuserid,
          projectid: project.projectid,
        };

        await axios.post(configData.CONTROLLERURL + configData.ADDARCHIVE, archiveData, {
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json;charset=UTF-8",
            "userid": state.user.userid,
            "usertoken": state.user.usertoken
          }
        }).then((res) => {
          console.log('Archive project data received');
          console.log(res.data);
          if (res.data instanceof Object) {
            if (res.data.code === 1) {
              handleCloseModal();
              navigation('/');
            }
            else {
              setResult(res.data.data);
            }
          }
        })

      }).catch((err) => {
        setResult(err.response?.data?.message || err.message || 'Network error');
      }).finally(() => {
        setLoading(false);
      });
    }

    return (
      <div className='modal_body'>
        <div className='modal_printable'>
          <div className='modal_row'>
            <div className='modal_rowsection'>Are you sure you want to archive project ?</div>
          </div>
        </div>
        <div className='modal_actions'>
          {result && <div className='modal_result'>{result}</div>}
          <div className='modal_buttons'>
            <div className='modal_buttoncontainer'>
              <button className="modal_button" onClick={archiveProject} disabled={loading}>{loading ? 'Loading...' : 'Archive'}</button>
            </div>
            <div className='modal_buttoncontainer'>
              <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function UnarchiveProject() {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState('');

    const unarchiveProject = () => {
      console.log('Trying to unarchive project');

      setResult('');

      setLoading(true);

      const data = {
        projectuserid: project.projectuserid,
        projectid: project.projectid,
      }

      axios.post(configData.CONTROLLERURL + configData.UNARCHIVE, data, {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json;charset=UTF-8",
          "userid": state.user.userid,
          "usertoken": state.user.usertoken
        }
      }).then((res) => {
        console.log('Unarchive project data received')
        console.log(res.data)
        if (res.data instanceof Object) {
          if (res.data.code === 1) {
            handleCloseModal();
            navigation('/');
          }
          else {
            setResult(res.data.data)
          }
        }
        else {
          setResult('Error');
        }
      }).catch((err) => {
        setResult(err.response?.data?.message || err.message || 'Network error');
      }).finally(() => {
        setLoading(false);
      });
    }

    return (
      <div className='modal_body'>
        <div className='modal_printable'>
          <div className='modal_row'>
            <div className='modal_rowsection'>Are you sure you want to unarchive project ?</div>
          </div>
        </div>
        <div className='modal_actions'>
          {result && <div className='modal_result'>{result}</div>}
          <div className='modal_buttons'>
            <div className='modal_buttoncontainer'>
              <button className="modal_button" onClick={unarchiveProject} disabled={loading}>{loading ? 'Loading...' : 'Unarchive'}</button>
            </div>
            <div className='modal_buttoncontainer'>
              <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function RemoveProject() {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState('');

    useEffect(() => {
      if (project.users && project.users.length) {
        setLoading(true);
        setResult('Users found. Remove them first before proceeding.')
      }
    }, [project]);

    const removeProject = () => {
      console.log('Trying to remove project');

      setResult('');

      const hasPermission = getPermission(
        project.projectuserid,
        project.users,
        state.user.userid,
        'Project',
        'remove',
        project.archived
      );
      if (hasPermission.code == 0) {
        setResult(hasPermission.data);
        return;
      }

      if (project.users.length > 0) {
        setResult('Please remove users first')
        return
      }

      if (project.clients.length > 0) {
        setResult('Please remove clients first')
        return
      }

      setLoading(true);

      const data = {
        projectuserid: project.projectuserid,
        projectid: project.projectid,
        archived: project.archived
      }

      axios.post(configData.CONTROLLERURL + configData.REMOVEPROJECT, data, {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json;charset=UTF-8",
          "userid": state.user.userid,
          "usertoken": state.user.usertoken
        }
      }).then((res) => {
        console.log('Remove project data received')
        console.log(res.data)
        if (res.data instanceof Object) {
          if (res.data.code == 1) {
            dispatch({
              type: 'REMOVE_PROJECT',
              projectid: project.id
            });
            navigation('/');
            handleCloseModal();
          }
          else {
            setResult(res.data.data)
          }
        }
        else {
          setResult('Error')
        }
      }).catch((err) => {
        setResult(err.response?.data?.message || err.message || 'Network error');
      }).finally(() => {
        setLoading(false);
      });
    }

    return (
      <div className='modal_body'>
        <div className='modal_printable'>
          <div className='modal_row'>
            <div className='modal_rowsection'>Are you sure you want to remove project ?</div>
          </div>
        </div>
        <div className='modal_actions'>
          {result && <div className='modal_result'>{result}</div>}
          <div className='modal_buttons'>
            <div className='modal_buttoncontainer'>
              {!loading && <button className="modal_button" onClick={removeProject} disabled={loading}>Remove</button>}
            </div>
            <div className='modal_buttoncontainer'>
              <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function DisconnectProject() {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState('');

    const disconnectProject = () => {
      console.log('Trying to remove project');

      setResult('');

      const hasPermission = getPermission(
        project.projectuserid,
        project.users,
        state.user.userid,
        'Project',
        'remove',
        project.archived
      );
      if (hasPermission.code == 0) {
        setResult(hasPermission.data);
        return;
      }

      setLoading(true);

      const data = {
        projectuserid: project.projectuserid,
        projectid: project.projectid,
        id: state.user.userid,
      }

      axios.post(configData.CONTROLLERURL + configData.REMOVEUSER, data, {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json;charset=UTF-8",
          "userid": state.user.userid,
          "usertoken": state.user.usertoken
        }
      }).then((res) => {
        console.log('Remove project data received')
        console.log(res.data)
        if (res.data.code === 1) {
          dispatch({
            type: 'REMOVE_PROJECT',
            projectid: project.id
          });
          navigation('/');
        }
      }).catch((err) => {
        setResult(err.response?.data?.message || err.message || 'Network error');
      }).finally(() => {
        setLoading(false);
      });
    }
    return (
      <div className='modal_body'>
        <div className='modal_printable'>
          <div className='modal_row'>
            <div className='modal_rowsection'>Are you sure you want to disconnect from project ?</div>
          </div>
        </div>
        <div className='modal_actions'>
          {result && <div className='modal_result'>{result}</div>}
          <div className='modal_buttons'>
            <div className='modal_buttoncontainer'>
              {!loading && <button className="modal_button" onClick={disconnectProject} disabled={loading}>Disconnect</button>}
            </div>
            <div className='modal_buttoncontainer'>
              <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const homepermission = getHomePermission(project.projectuserid, project.users, state.user.userid);

  const handleClickMenuItem = (selectedOption) => {
    setOption(selectedOption);
    setTogglemenu(false);
  };

  function Notice({ project }) {
    const [visible, setVisible] = useState(true);
    let notice = [];

    const now = moment();

    if (!project?.subscription || !visible) return null;

    const subscriptionid = project?.subscription?.subscriptionid;
    const username = project?.subscription?.username || '';
    const subscriptionstart = project?.subscription?.subscriptionstart || '';
    const subscriptionend = project?.subscription?.subscriptionend || '';

    const start = moment.unix(Number(subscriptionstart));
    const end = moment.unix(Number(subscriptionend));

    const expired = now.isAfter(end);

    if (expired) {
      if (subscriptionid != 'SUBDEMO') {
        let message = (username || 'Unknown User') + "'s subscription (" + start.format('D MMM YYYY') + " → " + end.format('D MMM YYYY') + ") has expired."
        notice.push(message);
      }
    }

    const assets = project.assets || [];
    const expiredExtinguishers = assets.filter(asset => {
      return (
        asset.type === 'fireextinguisher' &&
        asset.properties?.expiry &&
        moment(moment.unix(Number(asset.properties.expiry))).isBefore(now)
      );
    });

    if (expiredExtinguishers.length > 0) {
      notice.push(expiredExtinguishers.length + ' fire extinguisher(s) are expired.');
    }

    if (notice.length === 0 || project.archived == true) return null;

    return (
      <div className="home_notice">
        <div className="home_noticesection">
          {notice.map((item, index) => (
            <div key={index} className="home_noticesectiontext">
              {item}
            </div>
          ))}
        </div>
        <div className='home_noticesection'>
          <button onClick={() => setVisible(false)} className="button">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className='home'>
      <Header state={state} setState={setState} />
      <Notice project={project} />
      <div className='home_projectdetails'>
        <div className='home_projectsection'>
          <div className='home_projecttext'>
            {project.name}
          </div>
        </div>
        {homepermission['Project'] && <div className='home_projectsection'><button className='home_headerbutton' onClick={editProjectButton}>Edit Project</button></div>}
        {1 &&
          <div className='home_projectsection'>
            {project.archived == false && <div className='home_headerbutton' onClick={archiveProjectButton}>Archive</div>}
            {project.archived == true && <div className='home_headerbutton' onClick={unarchiveProjectButton}>Unarchive</div>}
          </div>
        }
        <div className='home_projectsection'><div className='home_headerbutton' onClick={downloadBackup}>Backup</div> </div>
        {homepermission['Project'] && project.type == 'localproject' && <div className='home_projectsection'><div className='home_headerremovebutton' onClick={removeButton}>Remove</div></div>}
        {homepermission['Project'] && project.type == 'sharedproject' && <div className='home_projectsection'><div className='home_headerremovebutton' onClick={disconnectButton}>Disconnect</div></div>}
      </div>

      <div className='home_content'>
        <div className={screenwidth > smallscreen ? 'home_menu' : (togglemenu ? 'home_menu home_menusmallvisible' : 'home_menu home_menusmallinvisible')} ref={homemenuRef}>
          {homepermission['Structure'] && <div className='home_button' onClick={() => handleClickMenuItem(<Summary state={state} setState={setState} />)}>Summary</div>}
          {homepermission['Assets'] && <div className='home_button' onClick={() => handleClickMenuItem(<Assets state={state} setState={setState} />)}>Assets ({assets.length})</div>}
          {homepermission['Readings'] && <div className='home_button' onClick={() => handleClickMenuItem(<Readings state={state} setState={setState} />)}>Readings ({readings.length})</div>}
          {homepermission['Structure'] && <div className='home_button' onClick={() => handleClickMenuItem(<Structure state={state} setState={setState} />)}>Structure</div>}
          {homepermission['Widgets'] && <div className='home_button' onClick={() => handleClickMenuItem(<Widgets state={state} setState={setState} />)}>Widgets ({widgets.length})</div>}
          {homepermission['Reports'] && <div className='home_button' onClick={() => handleClickMenuItem(<Reports state={state} setState={setState} />)}>Reports</div>}
          <div className='home_button' onClick={() => handleClickMenuItem(<Users state={state} setState={setState} />)}>Users ({users.length})</div>
          {homepermission['Settings'] && <div className='home_button' onClick={() => handleClickMenuItem(<Settings state={state} setState={setState} />)}>Settings</div>}
        </div>

        <div className='home_panel'>
          {option}
        </div>

        {
          screenwidth <= smallscreen &&
          <FloatingButton togglemenu={togglemenu} setTogglemenu={setTogglemenu} />
        }
      </div>
    </div>
  );
}

export default Home;